export const API_PRODUCT_CODES = {
  PB: 'PB',
  CR: 'CR',
  EAM: 'EAM',
  PRO: 'PRO',
} as const;

export const CREDIT_API_PRODUCT_CODE = API_PRODUCT_CODES.CR;
export const EAM_API_PRODUCT_CODE = API_PRODUCT_CODES.EAM;
export const COUNTRY_MANUAL_SUPPORTED_API_CODES: ReadonlySet<string> = new Set([
  API_PRODUCT_CODES.PB,
  API_PRODUCT_CODES.CR,
  API_PRODUCT_CODES.EAM,
  API_PRODUCT_CODES.PRO
]);

export const FILE_BY_API_ENUMS = {
  MEMORANDUM: 'MEMORANDUM',
  DOS_AND_DONTS: 'DOS_AND_DONTS',
  BUSINESS_RELATIONSHIP: 'BUSINESS_RELATIONSHIP',
} as const;

export type FileByApiEnum = typeof FILE_BY_API_ENUMS[keyof typeof FILE_BY_API_ENUMS];

const FILE_BY_API_ENUMS_FOR_PB: Array<FileByApiEnum> = [
  FILE_BY_API_ENUMS.MEMORANDUM,
  FILE_BY_API_ENUMS.DOS_AND_DONTS,
  FILE_BY_API_ENUMS.BUSINESS_RELATIONSHIP,
];

const FILE_BY_API_ENUMS_FOR_CR: Array<FileByApiEnum> = [
  FILE_BY_API_ENUMS.MEMORANDUM,
  FILE_BY_API_ENUMS.DOS_AND_DONTS,
];

const FILE_BY_API_ENUMS_FOR_EAM: Array<FileByApiEnum> = [
  FILE_BY_API_ENUMS.MEMORANDUM,
  FILE_BY_API_ENUMS.DOS_AND_DONTS,
];

const FILE_BY_API_ENUMS_FOR_PRO: Array<FileByApiEnum> = [
  FILE_BY_API_ENUMS.MEMORANDUM,
  FILE_BY_API_ENUMS.DOS_AND_DONTS,
];

export const normalizeApiProductCode = (apiProductCode?: string | null): string | null => {
  const normalized = apiProductCode?.trim().toUpperCase();
  return normalized || null;
};

export const normalizeCountryManualId = (value: unknown): number | null => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

/** Country-manual version ids use the same positive-integer shape as manual ids. */
export const normalizeVersionId = normalizeCountryManualId;

export const isCreditApiProduct = (apiProductCode?: string | null): boolean => {
  return normalizeApiProductCode(apiProductCode) === CREDIT_API_PRODUCT_CODE;
};

export const isEamApiProduct = (apiProductCode?: string | null): boolean => {
  return normalizeApiProductCode(apiProductCode) === EAM_API_PRODUCT_CODE;
};

export const isProApiProduct = (apiProductCode?: string | null): boolean => {
  return normalizeApiProductCode(apiProductCode) === API_PRODUCT_CODES.PRO;
};

/** Column label for bank/company domicile (EAM uses "Company Domicile"). */
export const getBankDomicileColumnLabel = (apiProductCode?: string | null): string =>
  isEamApiProduct(apiProductCode) ? 'Company Domicile' : 'Bank Domicile';

export const isCountryManualSupportedApiProduct = (apiProductCode?: string | null): boolean => {
  const normalizedApiProductCode = normalizeApiProductCode(apiProductCode);
  return normalizedApiProductCode != null && COUNTRY_MANUAL_SUPPORTED_API_CODES.has(normalizedApiProductCode);
};

export const getAllowedFileByApiEnums = (apiProductCode?: string | null): Array<FileByApiEnum> => {
  if (isProApiProduct(apiProductCode)) {
    return FILE_BY_API_ENUMS_FOR_PRO;
  }
  if (isCreditApiProduct(apiProductCode)) {
    return FILE_BY_API_ENUMS_FOR_CR;
  }
  if (isEamApiProduct(apiProductCode)) {
    return FILE_BY_API_ENUMS_FOR_EAM;
  }
  return FILE_BY_API_ENUMS_FOR_PB;
};

export const isFileByApiEnumAllowed = (
  fileByApiEnum?: string | null,
  apiProductCode?: string | null
): boolean => {
  if (!fileByApiEnum) return false;
  return getAllowedFileByApiEnums(apiProductCode).includes(fileByApiEnum as FileByApiEnum);
};

export const filterFileByApiItemsByProduct = <T>(
  items: Array<T>,
  readEnum: (item: T) => string | null | undefined,
  apiProductCode?: string | null
): Array<T> => {
  return items.filter((item) => isFileByApiEnumAllowed(readEnum(item), apiProductCode));
};
